/**
 * Component for our Analytics section displaying top 10 returned customers.
 *
 * @component
 */

import { message, Typography, Button, Col, Row, Menu, Dropdown} from "antd";
import axios from "axios";
import React from 'react';
import { CSVLink } from 'react-csv';
import { Parser } from 'json2csv';
import MostReturnedTable from './MostReturnTable';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Text } = Typography;

const exportHeaders = [
  {
    label: 'Shipping Name',
    value: 'shippingName',
    key: 'shippingName',
  },
  {
    label: 'Customer Email',
    value: 'customerEmail',
    key: 'customerEmail',
  },
  {
    label: 'Count',
    value: 'count',
    key: 'count',
  }
];

class MostReturn extends React.Component {
  constructor() {
    super()
    this.state = {
      mostReturns: [],
      dataCSVToBeExported: [],
      exportLoading: false,
      pdfLoading: false,
      timestamp : Date.now(),
    };
    this.printRef = React.createRef();
    this.csvLinkRef = React.createRef();
  }

  handleExportPdf = async () => {
    this.setState({ pdfLoading: true });
    
    try {
      if (this.state.mostReturns.length === 0) {
        this.setState({ pdfLoading: false });
        return message.error('There is no data to export as PDF');
      }

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.width;
      pdf.setFontSize(16);
      const title = 'Top Customers by Return Count';
      const titleWidth = pdf.getStringUnitWidth(title) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
      pdf.text(title, (pageWidth - titleWidth) / 2, 40);
      
      pdf.setFontSize(12);
      const dateText = `Generated on: ${new Date().toLocaleDateString()}`;
      const dateWidth = pdf.getStringUnitWidth(dateText) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
      pdf.text(dateText, (pageWidth - dateWidth) / 2, 60);

      const headers = exportHeaders.map(h => h.label);
      const tableData = this.state.mostReturns.map(row => 
        exportHeaders.map(header => row[header.value] || '')
      );

      const tableWidth = 430; 
      const leftMargin = (pageWidth - tableWidth) / 2;

      autoTable(pdf, {
        head: [headers],
        body: tableData,
        startY: 80,
        margin: { 
          top: 80, 
          left: leftMargin, 
          right: leftMargin 
        },
        tableWidth: tableWidth,
        styles: {
          fontSize: 10,
          cellPadding: { top: 8, right: 5, bottom: 8, left: 5 },
          minCellHeight: 25, 
          halign: 'center',
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'left',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        theme: 'striped',
        showHead: 'everyPage',
        columnStyles: {
          0: { cellWidth: 150, halign: 'left' }, 
          1: { cellWidth: 200, halign: 'left' }, 
          2: { cellWidth: 80, halign: 'left' },  
        },
      });

      pdf.save(`RichReturns-MostReturn-${this.state.timestamp}.pdf`);
      message.success(`PDF downloaded successfully with ${this.state.mostReturns.length} records`);
    } catch (error) {
      console.error('PDF export error:', error);
      this.setState({ pdfLoading: false });
      return message.error('Failed to generate PDF');
    } finally {
      this.setState({ pdfLoading: false });
    }
  };

  async handleExportCsv() {
    this.setState({
      exportLoading: true,
    });

    try {
      if (!this.state.mostReturns) {
        this.setState({
          exportLoading: false,
        });
        return message.error('Error creating export file of the report.', 4);
      }

      const dataParser = new Parser({
        fields: exportHeaders,
        quote: '' 
      });
      const cleanedData = this.state.mostReturns.map(({ shippingName, customerEmail, count }) => ({
        shippingName,
        customerEmail,
        count
      }));

      const dataCSVToBeExported = dataParser.parse(cleanedData);

      this.setState({
        exportLoading: false,
        dataCSVToBeExported,
      });

      setTimeout(() => {
        if (this.csvLinkRef.current && this.state.dataCSVToBeExported.length > 0) {
          this.csvLinkRef.current.link.click();
          message.success('CSV downloaded successfully!', 2); 
          this.handleResetDataToBeExported();
        }
      }
      , 1000);
    } catch (err) {
      this.setState({
        exportLoading: false,
      });
      return message.error('Error creating report csv.', err);
    }
  }
  handleResetDataToBeExported = () => {
    this.setState({
      dataCSVToBeExported: [],
    });
  };
  async componentDidMount() {
    try {
      const response = await axios.get("/api/analytics/customer/most-returned");
      if (response?.data.status === "error") {
        return message.error("Error while fetching records of most returned customers .");
      }
      const returnData = response.data
      this.setState({
        mostReturns: returnData
      })
    } catch (err) {
      return message.error("Error while fetching records of most  returned customers .");
    }
  }
  render() {
    return (
      <React.Fragment>
        {/* <Row
          type="flex"
          justify="start"
          align="top"
        >
          <Col>
            <h1 className="TextHeading TextHeading--sm">Customers with Most Returns</h1>
            <p>List of top 10 Customers with most Returns</p>
          </Col>
        </Row> */}

        <Row type="flex" justify="space-between" align="middle" gutter={16} style={{ marginBottom: 20 }}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 18 }}
            md={{ span: 18 }}
            lg={{ span: 18 }}
          >
            <Text 
              style={{ 
                fontSize: 14,
                display: 'flex',
                alignItems: 'center',
                height: '32px' // Match Button height
              }}
            >
              List of top 10 Customers with most Returns
            </Text>
          </Col>

          <Col
            xs={{ span: 24 }}
            sm={{ span: 6 }}
            md={{ span: 6 }}
            lg={{ span: 6 }}
          >
            {this.state.dataCSVToBeExported.length > 0 && 
              <CSVLink
                data={this.state.dataCSVToBeExported}
                filename={`RichReturns-MostReturn-${this.state.timestamp}.csv`}
                className="hidden"
                ref={this.csvLinkRef}
              />
            }
            
            {this.state.mostReturns.length > 0 && 
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item key="csv" onClick={this.handleExportCsv.bind(this)} disabled={this.state.exportLoading}>
                      Export CSV
                    </Menu.Item>
                    <Menu.Item key="pdf" onClick={this.handleExportPdf} disabled={this.state.pdfLoading}>
                      Export PDF
                    </Menu.Item>
                  </Menu>
                }
                placement="bottomRight"
              >
                <Button block={true}>
                  Export
                </Button>
              </Dropdown>  
            }
          </Col>
        </Row>
        <div ref={this.printRef}>
          <MostReturnedTable
            mostReturns={this.state.mostReturns}
          />
        </div>
      </React.Fragment>
    )
  }
}
export default MostReturn;